/**
 * Security Audit Page
 * View security audit logs and active sessions (Director only)
 */
import { useState, useEffect, useCallback } from 'react';
import { Shield, Users, Clock, AlertTriangle, RefreshCw, UserX, LogIn, LogOut, ShieldAlert, Activity } from 'lucide-react';
import { useApp } from '../context/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityAuditPage = () => {
  const { userRole } = useApp();
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs');
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [logsRes, sessionsRes] = await Promise.all([
        fetch(`${API_URL}/api/auth/audit-logs?limit=100`, { credentials: 'include' }),
        fetch(`${API_URL}/api/auth/active-sessions`, { credentials: 'include' })
      ]);

      if (!logsRes.ok || !sessionsRes.ok) {
        throw new Error('Gagal mendapatkan data keselamatan');
      }

      const logsData = await logsRes.json();
      const sessionsData = await sessionsRes.json();

      setAuditLogs(logsData.audit_logs || []);
      setActiveSessions(sessionsData.active_sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRevokeSession = async (userId, userName) => {
    if (!window.confirm(`Adakah anda pasti mahu batalkan sesi untuk ${userName}?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/auth/session/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error revoking session:', err);
    }
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'LOGIN_SUCCESS': return <LogIn className="w-4 h-4 text-green-500" />;
      case 'LOGIN_DENIED': return <ShieldAlert className="w-4 h-4 text-red-500" />;
      case 'LOGOUT': return <LogOut className="w-4 h-4 text-blue-500" />;
      case 'UNAUTHORIZED_AUDIT_ACCESS': return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'SESSION_REVOKED': return <UserX className="w-4 h-4 text-purple-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventBadge = (eventType) => {
    const badges = {
      'LOGIN_SUCCESS': 'bg-green-100 text-green-700',
      'LOGIN_DENIED': 'bg-red-100 text-red-700',
      'LOGOUT': 'bg-blue-100 text-blue-700',
      'UNAUTHORIZED_AUDIT_ACCESS': 'bg-orange-100 text-orange-700',
      'SESSION_REVOKED': 'bg-purple-100 text-purple-700'
    };
    return badges[eventType] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ms-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Access denied for non-directors
  if (userRole !== 'director') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="access-denied">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Akses Ditolak</h2>
          <p className="text-gray-500">Hanya Director boleh mengakses halaman ini.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-audit-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="w-7 h-7 text-red-700" />
            Audit Keselamatan
          </h1>
          <p className="text-gray-500 text-sm mt-1">Pantau aktiviti keselamatan sistem</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Muat Semula
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-green-500">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <LogIn className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Login Berjaya</p>
              <p className="text-xl font-bold text-gray-800">
                {auditLogs.filter(l => l.event_type === 'LOGIN_SUCCESS').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ShieldAlert className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Login Ditolak</p>
              <p className="text-xl font-bold text-gray-800">
                {auditLogs.filter(l => l.event_type === 'LOGIN_DENIED').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sesi Aktif</p>
              <p className="text-xl font-bold text-gray-800">{activeSessions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('logs')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'logs'
                ? 'text-red-700 border-b-2 border-red-700 bg-red-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-logs"
          >
            <Activity className="w-4 h-4" />
            Log Aktiviti
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'sessions'
                ? 'text-red-700 border-b-2 border-red-700 bg-red-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-sessions"
          >
            <Users className="w-4 h-4" />
            Sesi Aktif
          </button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-red-700 animate-spin mx-auto mb-2" />
            <p className="text-gray-500">Memuat data...</p>
          </div>
        ) : activeTab === 'logs' ? (
          /* Audit Logs */
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {auditLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                Tiada log aktiviti
              </div>
            ) : (
              auditLogs.map((log, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50 flex items-start gap-3" data-testid={`log-${idx}`}>
                  <div className="mt-1">{getEventIcon(log.event_type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getEventBadge(log.event_type)}`}>
                        {log.event_type?.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {log.user_email || 'Unknown'}
                      </span>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {JSON.stringify(log.details)}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(log.timestamp)}
                      </span>
                      {log.ip_address && (
                        <span>IP: {log.ip_address}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Active Sessions */
          <div className="divide-y">
            {activeSessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                Tiada sesi aktif
              </div>
            ) : (
              activeSessions.map((session, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50 flex items-center justify-between gap-4" data-testid={`session-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{session.name || session.email}</p>
                      <p className="text-sm text-gray-500">{session.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase ${
                          session.role === 'director' ? 'bg-purple-100 text-purple-700' :
                          session.role === 'finance' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {session.role}
                        </span>
                        <span className="text-xs text-gray-400">
                          Aktif hingga: {formatDate(session.expires_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeSession(session.user_id, session.name || session.email)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Batalkan sesi"
                    data-testid={`revoke-${idx}`}
                  >
                    <UserX className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityAuditPage;
